import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Stack,
  Button,
  Link,
} from '@mui/material';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminIcon,
  Edit as EditIcon,
  Language as LanguageIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, useUpdateProfile, useChangePassword } from '../hooks/useAuth';
import AccountSettings from './profile/AccountSettings';
import SecuritySettings from './profile/SecuritySettings';
import PreferencesSettings from './profile/PreferencesSettings';
import NotificationsSettings from './profile/NotificationsSettings';
import PageSection from '../components/PageSection';

function UserProfile() {
  const { t } = useTranslation(['profile', 'common']);
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [activeTab, setActiveTab] = useState<'account' | 'security' | 'preferences' | 'notifications'>('account');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const { data: user, isLoading: loadingUser } = useCurrentUser() as { data: any; isLoading: boolean };
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      await updateProfile.mutateAsync(profileData);
      setSuccess(t('profile:personalInfo.success'));
      setIsEditing(false);
    } catch (error: any) {
      setError(error.response?.data?.detail || t('profile:personalInfo.error'));
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError(t('profile:security.errors.mismatch'));
      return;
    }
    
    try {
      await changePassword.mutateAsync({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      setSuccess(t('profile:security.success'));
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error: any) {
      setError(error.response?.data?.detail || t('profile:security.errors.currentIncorrect'));
    }
  };


  const handleEdit = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError('');
    setSuccess('');
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || ''
      });
    }
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setError('');
    setSuccess('');
  };

  if (loadingUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('profile:loadError')}
        </Alert>
      </Box>
    );
  }

  return (
    <PageSection
      title={t('profile:title')}
      subtitle={t('profile:subtitle')}
      hideTitleOnMobile
    >
      <Grid container spacing={3}>
        {/* Left Column - User Info */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ 
            height: 'fit-content',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 2,
                  fontSize: '2rem',
                  background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                }}
              >
                {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {user?.email}
              </Typography>
              <Chip
                icon={user?.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
                label={user?.role || 'User'}
                color={user?.role === 'admin' ? 'error' : 'primary'}
                variant="filled"
                sx={{ 
                  borderRadius: 1.5,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  py: 2.5
                }}
              />

              <Divider sx={{ my: 4 }} />

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
                  {t('profile:status.title')}
                </Typography>
                <Stack spacing={2.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t('profile:status.emailVerification')}
                      </Typography>
                    </Box>
                    <Chip
                      icon={user?.is_verified ? <CheckIcon sx={{ fontSize: 16 }} /> : <WarningIcon sx={{ fontSize: 16 }} />}
                      label={user?.is_verified ? t('profile:account.verified') : t('profile:account.notVerified')}
                      color={user?.is_verified ? 'success' : 'warning'}
                      variant="filled"
                      size="small"
                      sx={{ 
                        borderRadius: 1.5,
                        fontWeight: 600,
                        minWidth: 80
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <SecurityIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t('profile:status.accountStatus')}
                      </Typography>
                    </Box>
                    <Chip
                      icon={user?.is_active ? <CheckIcon sx={{ fontSize: 16 }} /> : <WarningIcon sx={{ fontSize: 16 }} />}
                      label={user?.is_active ? t('profile:status.active') : t('profile:status.inactive')}
                      color={user?.is_active ? 'success' : 'error'}
                      variant="filled"
                      size="small"
                      sx={{ 
                        borderRadius: 1.5,
                        fontWeight: 600,
                        minWidth: 80
                      }}
                    />
                  </Box>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Profile Settings */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card sx={{ 
            height: 'fit-content',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {t('profile:profileSettings')}
                </Typography>
                {activeTab === 'account' && !isEditing && (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                    sx={{ 
                      borderRadius: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd8 0%, #d94f7e 100%)',
                      }
                    }}
                  >
                    {t('profile:editProfile')}
                  </Button>
                )}
              </Box>

              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                sx={{ mb: 3 }}
                TabIndicatorProps={{
                  sx: {
                    background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                    height: 3,
                    borderRadius: 1.5
                  }
                }}
              >
                <Tab
                  value="account"
                  label={t('profile:account.title')}
                  icon={<PersonIcon />}
                  iconPosition="start"
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': {
                      color: 'primary.main'
                    }
                  }}
                />
                <Tab
                  value="security"
                  label={t('profile:security.title')}
                  icon={<LockIcon />}
                  iconPosition="start"
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': {
                      color: 'primary.main'
                    }
                  }}
                />
                <Tab
                  value="preferences"
                  label={t('profile:preferences.title')}
                  icon={<LanguageIcon />}
                  iconPosition="start"
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': {
                      color: 'primary.main'
                    }
                  }}
                />
                <Tab
                  value="notifications"
                  label={t('profile:notifications.title', { defaultValue: 'Notifications' })}
                  icon={<NotificationsIcon />}
                  iconPosition="start"
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': {
                      color: 'primary.main'
                    }
                  }}
                />
              </Tabs>

              {activeTab === 'account' && (
                <AccountSettings
                  user={user}
                  profileData={profileData}
                  isEditing={isEditing}
                  error={error}
                  success={success}
                  onProfileChange={handleProfileChange}
                  onProfileSubmit={handleProfileSubmit}
                  onEdit={handleEdit}
                  onCancel={handleCancel}
                  isUpdating={updateProfile.isPending}
                />
              )}

              {activeTab === 'security' && (
                <SecuritySettings
                  passwordData={passwordData}
                  error={error}
                  success={success}
                  onPasswordChange={handlePasswordChange}
                  onPasswordSubmit={handlePasswordSubmit}
                  onCancel={handlePasswordCancel}
                  isChanging={changePassword.isPending}
                />
              )}

              {activeTab === 'preferences' && (
                <PreferencesSettings />
              )}

              {activeTab === 'notifications' && (
                <NotificationsSettings />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageSection>
  );
}

export default UserProfile;
